// Volume Profile — horizontal histogram of traded volume by price level, docked to the
// right edge of the price pane. DISPLAY-ONLY, additive; does not touch scoring. Built on
// lightweight-charts v4.2's primitives API (attachPrimitive/ISeriesPrimitive) since volume
// profile is not a built-in series type in this library.
//
// v1 simplification: each candle's FULL volume is assigned to the bucket containing its
// midpoint price (high+low)/2 — not split proportionally across the candle's high/low
// range. Good enough to show where volume concentrated; a proportional-split refinement
// is a later polish, not required for the indicator to be useful.

// Bucket the given candles' volume into `bins` equal price-height buckets across the
// candles' own high/low range. Returns { bins: [{priceLow, priceHigh, volume}], maxVolume }.
export function computeVolumeProfile(candles, bins = 24) {
  if (!candles || candles.length === 0) {
    return { bins: [], maxVolume: 0 };
  }
  let minPrice = Infinity;
  let maxPrice = -Infinity;
  for (const c of candles) {
    if (c.low < minPrice) minPrice = c.low;
    if (c.high > maxPrice) maxPrice = c.high;
  }
  if (!Number.isFinite(minPrice) || !Number.isFinite(maxPrice) || maxPrice <= minPrice) {
    return { bins: [], maxVolume: 0 };
  }
  const bucketSize = (maxPrice - minPrice) / bins;
  const buckets = Array.from({ length: bins }, (_, i) => ({
    priceLow: minPrice + i * bucketSize,
    priceHigh: minPrice + (i + 1) * bucketSize,
    volume: 0,
  }));
  for (const c of candles) {
    const mid = (c.high + c.low) / 2;
    let idx = Math.floor((mid - minPrice) / bucketSize);
    if (idx < 0) idx = 0;
    if (idx >= bins) idx = bins - 1;
    buckets[idx].volume += c.volume || 0;
  }
  const maxVolume = buckets.reduce((m, b) => (b.volume > m ? b.volume : m), 0);
  return { bins: buckets, maxVolume };
}

// --- Canvas renderer: draws right-anchored horizontal bars, one per price bucket. ---
class VolumeProfileRenderer {
  constructor(bars, maxBarWidthRatio) {
    this._bars = bars;
    this._maxBarWidthRatio = maxBarWidthRatio;
  }
  draw(target) {
    const bars = this._bars;
    if (!bars || !bars.length) return;
    target.useMediaCoordinateSpace(({ context: ctx, mediaSize }) => {
      const maxBarWidth = Math.min(140, mediaSize.width * this._maxBarWidthRatio);
      ctx.save();
      for (const bar of bars) {
        if (bar.widthRatio <= 0) continue;
        const w = bar.widthRatio * maxBarWidth;
        const h = Math.max(1, bar.yBottom - bar.yTop);
        ctx.fillStyle = bar.isPoc ? "rgba(251,191,36,0.55)" : "rgba(129,140,248,0.32)";
        ctx.fillRect(mediaSize.width - w, bar.yTop, w, h);
      }
      ctx.restore();
    });
  }
}

class VolumeProfilePaneView {
  constructor(getData) {
    this._getData = getData;
    this._bars = [];
  }
  update() {
    const { bins, maxVolume, series } = this._getData();
    if (!series || !bins.length || maxVolume <= 0) {
      this._bars = [];
      return;
    }
    this._bars = bins.map((b) => {
      const yTop = series.priceToCoordinate(b.priceHigh);
      const yBottom = series.priceToCoordinate(b.priceLow);
      return {
        yTop: yTop ?? 0,
        yBottom: yBottom ?? 0,
        widthRatio: b.volume / maxVolume,
        isPoc: b.volume === maxVolume,
      };
    }).filter((b) => yFinite(b));
  }
  renderer() {
    return new VolumeProfileRenderer(this._bars, 0.28);
  }
}

const yFinite = (b) => Number.isFinite(b.yTop) && Number.isFinite(b.yBottom);

// Attach to a candlestick series via `series.attachPrimitive(primitive)`. `getProfile` is a
// () => { bins, maxVolume } closure so the primitive always reads the latest computed
// profile without needing to be re-instantiated when data changes within the same chart.
export class VolumeProfilePrimitive {
  constructor(getProfile) {
    this._getProfile = getProfile;
    this._series = null;
    this._paneViews = [new VolumeProfilePaneView(() => ({ ...this._getProfile(), series: this._series }))];
  }
  attached({ series }) {
    this._series = series;
  }
  detached() {
    this._series = null;
  }
  updateAllViews() {
    this._paneViews.forEach((pv) => pv.update());
  }
  paneViews() {
    return this._paneViews;
  }
}
